/**
 * Lightweight tooltip primitive — wraps Radix.
 *
 * Use for hover-help on small UI controls (e.g. an "i" icon next to a
 * form field). Open / close defaults are kept terse (150 ms delay) so
 * the tooltip feels responsive but doesn't flash on transient hovers.
 *
 * For a richer popover with click-to-open and arbitrary content use
 * the Popover primitive instead — this tooltip is intentionally
 * passive (text-only, hover/focus-triggered).
 */
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/cn";


/** Render a single TooltipProvider at the app root once. */
export const TooltipProvider = TooltipPrimitive.Provider;


export interface TooltipProps {
  /** Element that triggers the tooltip on hover/focus. */
  children: React.ReactNode;
  /** What to show inside the tooltip. Keep it short — for long help
   *  use a Popover. */
  content: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}


export function Tooltip({ children, content, side = "top", align = "center" }: TooltipProps) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root delayDuration={150}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={4}
          className={cn(
            "z-50 max-w-xs rounded-md border border-border/60 bg-elevated px-2 py-1.5",
            "text-[11px] leading-snug text-foreground shadow-md",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
